import React, { useState } from "react";
import { Formik, FormikErrors } from "formik";
import {
  TextField,
  PrimaryButton,
  Dialog,
  DialogType,
  Text,
  Label,
} from "@fluentui/react";
import PasswordPolicy from "../common/PasswordPolicy";
import { ToastContext } from "../common/ToastProvider";
import { EMAIL_REGEX } from "../common/Constants";
import {
  requestForgotPasswordOtp,
  resetForgotPassword,
  validateForgotPasswordOtp,
} from "../../services/datacenter/DataCenterManagerService";
import { isValidPassword } from "../../services/UtilService";

export interface ForgotPasswordProps {
  onClose: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onClose }) => {
  const [step, setStep] = useState(1 as number);
  const [email, setEmail] = useState("" as string);
  const [otp, setOtp] = useState("" as string);
  const [loading, setLoading] = useState(false as boolean);
  const [normalUser, setNormalUser] = useState(false as boolean);

  //hooks
  const { showToast } = React.useContext(ToastContext);

  return (
    <React.Fragment>
      <Dialog
        hidden={false}
        onDismiss={onClose}
        modalProps={{
          isBlocking: true,
          styles: {
            main: {
              minWidth: "350px !important",
              maxWidth: "450px !important",
            },
          },
        }}
        dialogContentProps={{
          type: DialogType.normal,
          title: <Text className="sign-in-text">Forgot Password</Text>,
        }}
      >
        {step === 1 && (
          <Formik
            initialValues={{ email: "" }}
            validate={(values) => {
              let errors: FormikErrors<{ email?: string }> = {};
              if (!values.email) {
                errors.email = "Required";
              } else if (values.email && !EMAIL_REGEX.test(values.email)) {
                errors.email = "Invalid email address";
              }

              return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setEmail(values.email);
              setLoading(true);
              const result = await requestForgotPasswordOtp(values.email);
              setLoading(false);
              if (result.success) {
                showToast("OTP sent to your email", "success");
                setStep(2);
              } else {
                showToast(
                  result.message ||
                    "Error while sending you the OTP, please try again",
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Label className="login-label mt-8">Email Address</Label>
                <TextField
                  id="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={
                    touched.email && errors.email ? errors.email : ""
                  }
                  underlined
                />
                <br />
                <PrimaryButton
                  type="submit"
                  text="Send OTP"
                  disabled={isSubmitting || loading}
                  className="login-btn"
                />
              </form>
            )}
          </Formik>
        )}
        {step === 2 && (
          <Formik
            initialValues={{ otp: "" }}
            validate={(values) => {
              let errors: FormikErrors<{ otp?: string }> = {};
              if (!values.otp) {
                errors.otp = "Required";
              } else if (values.otp.length !== 6) {
                errors.otp = "OTP must be of 6 digits";
              }

              return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setOtp(values.otp);
              setLoading(true);
              const result = await validateForgotPasswordOtp(email, values.otp);
              setLoading(false);
              if (result.success) {
                showToast("OTP verified", "success");
                setNormalUser(result.data.normalUser);
                setStep(3);
              } else {
                showToast(
                  result.message || "Error while verifying OTP",
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Label className="login-label mt-8">
                  One Time Password (OTP)
                </Label>
                <TextField
                  id="otp"
                  name="otp"
                  autoFocus
                  value={values.otp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={touched.otp && errors.otp ? errors.otp : ""}
                  underlined
                />
                <br />
                <PrimaryButton
                  type="submit"
                  text="Verify OTP"
                  disabled={isSubmitting || loading}
                  className="login-btn"
                />
              </form>
            )}
          </Formik>
        )}
        {step === 3 && (
          <Formik
            initialValues={{ newPassword: "", confirmPassword: "" }}
            validate={(values) => {
              let errors: FormikErrors<{
                newPassword?: string;
                confirmPassword?: string;
              }> = {};
              if (!values.newPassword) {
                errors.newPassword = "Required";
              }
              if (values.newPassword && !isValidPassword(values.newPassword, !normalUser)) {
                errors.newPassword = "Please enter valid password.";
              }
              if (!values.confirmPassword) {
                errors.confirmPassword = "Required";
              }
              if (
                values.newPassword &&
                values.confirmPassword &&
                values.newPassword !== values.confirmPassword
              ) {
                errors.confirmPassword = "Password does not match";
              }
              return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setLoading(true);
              const result = await resetForgotPassword(
                email,
                otp,
                values.newPassword
              );
              if (result.success) {
                showToast("Password reset successful, please login", "success");
                setTimeout(() => onClose(), 2000);
              } else {
                showToast(
                  result.message || "Error while resetting password",
                  "error"
                );
                setLoading(false);
              }
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Label className="login-label mt-8">New Password</Label>
                <TextField
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={
                    touched.newPassword && errors.newPassword
                      ? errors.newPassword
                      : ""
                  }
                  underlined
                />
                {errors.newPassword &&
                  touched.newPassword &&
                  values.newPassword && (
                    <PasswordPolicy password={values.newPassword} isAdmin={!normalUser} />
                  )}
                <Label className="login-label">Confirm Password</Label>
                <TextField
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={
                    touched.confirmPassword && errors.confirmPassword
                      ? errors.confirmPassword
                      : ""
                  }
                  underlined
                />
                <br />
                <PrimaryButton
                  type="submit"
                  text="Reset Password"
                  disabled={isSubmitting || loading}
                  className="login-btn"
                />
              </form>
            )}
          </Formik>
        )}
      </Dialog>
    </React.Fragment>
  );
};

export default ForgotPassword;
